import React from 'react'
import {
    Alert,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import AppDialog from '../../dialog/AppDialog'

const model = ['Model', 'Required text.', 'MODEL-200']
const assembly = ['Assembly', 'Required text.', 'A']
const oldPrice = ['Old price', 'Required numeric old price.', 1000]
const varcond = ['Varconds', 'SAP variant condition.', 'VARCOND_EXAMPLE']
const kmat = ['Kmat', 'SAP material code.', 'KMAT_EXAMPLE']
const price = ['Price', 'Current price shown by the table; ignored by this import. It can be left blank.', '']

const formats = {
    units: {
        title: 'Units',
        columns: [model, assembly, ['Refrigerant type', 'Required text.', 'R32'], oldPrice, varcond, kmat, price],
        note: 'Varconds and Kmat must contain text for unit rows. An empty old price is not accepted.',
    },
    options: {
        title: 'Options',
        columns: [
            model,
            assembly,
            ['Label', 'Required text.', 'Heating'],
            ['Value', 'Required text or number.', '1'],
            ['Type', 'Required text.', 'Heating'],
            oldPrice,
            varcond,
            kmat,
            price,
        ],
        note: 'Old price must be an Excel numeric cell, not text. Keep all columns through Kmat, even when SAP metadata is blank.',
    },
    fans: {
        title: 'Fans',
        columns: [
            model,
            assembly,
            ['Type', 'Required text.', 'Supply'],
            ['Label', 'Text; an empty label is allowed.', 'Supply fan'],
            ['Technology', 'Required text.', 'EC'],
            oldPrice,
            varcond,
            kmat,
            price,
        ],
        note: 'An empty old price is not accepted. Keep all columns through Kmat, even when SAP metadata is blank.',
    },
    wheels: {
        title: 'Wheels',
        columns: [
            model,
            assembly,
            ['size', 'Required size.', '500'],
            ['Material', 'Required text.', 'Aluminium'],
            ['Channel', 'Required numeric channel spacing.', 2.5],
            ['Control Type', 'Blank defaults to Standard.', 'Standard'],
            ['Old Price', 'Optional numeric old price; blank imports a null old price.', ''],
            varcond,
            kmat,
            price,
        ],
        note: 'Wheels can be imported without an old price. The current Price column is not imported.',
    },
    airflows: {
        title: 'Airflows',
        columns: [model, assembly, ['AirFlow', 'Required numeric airflow.', 5000], oldPrice, varcond, kmat, price],
        note: 'An empty old price is not accepted. Keep all columns through Kmat, even when SAP metadata is blank.',
    },
    qm: {
        title: 'QM',
        columns: [
            ['Product Type', 'Required product type.', '04N_'],
            ['Size', 'Required size.', '200'],
            assembly,
            ['Varcode', 'Required variant code.', 'VAR'],
            ['Price', 'Optional numeric price; blank imports a null price.', ''],
            ['Varcond', 'SAP metadata shown in downloads; ignored by this import.', ''],
        ],
        note: 'QM accepts an empty Price. Arrange columns in the order below before importing; the on-screen and downloaded column order can differ.',
    },
    control: {
        title: 'Controls',
        columns: [
            ['Name', 'Required text.', 'Control example'],
            ['Price', 'Required numeric price.', 100],
        ],
        note: 'Controls require a numeric Price. An empty price is not accepted.',
    },
}

const sectionStyles = { mt: 2, mb: 1 }

function ImportHelp({ api, open, onClose }) {
    const destination = api.replace(/\/+$/, '').split('/').pop().toLowerCase()
    const format = formats[destination]

    return (
        <AppDialog
            open={open}
            onClose={onClose}
            title={`Upload new prices - ${format?.title || 'Rooftop'} help`}
            showCloseButton
            actions={<Button onClick={onClose}>Close</Button>}
        >
            <Alert severity='info'>
                Choose how to import in the upload dialog. <strong>Add new rows</strong> keeps the current table and
                updates existing SAP metadata when Varcond or Kmat changes. The uploaded Old price is imported
                and the current price is cleared until SAP sync. Unchanged keys are ignored; QM only adds new keys.
                <strong>Replace the whole table</strong> removes every
                current row, including those absent from the file: download and back up current prices first.
            </Alert>
            <Typography variant='h6' component='h3' sx={sectionStyles}>
                What this button does
            </Typography>
            <Typography>
                Upload new prices reads a file and adds its validated rows to the current rooftop table, or replaces the
                table with them. In Add mode, the first valid file row for a key wins. Existing rows are updated only
                when Varcond or Kmat changes (Units, Wheels, Fans, Airflows and Options): the uploaded SAP metadata
                and Old price replace the stored values and the current price is cleared. Run Sync all rooftops
                prices afterwards. Otherwise existing rows and prices are preserved. QM keeps its add-only behavior.
                Business keys are Units: model, assembly, refrigerant; Wheels: model, assembly, size, material,
                channel spacing, control type; Fans: model, assembly, type, label, technology; Airflows: model,
                assembly, airflow; Options: model, assembly, label, value, type; QM: product type, size, assembly,
                varcode. It does not request prices from SAP. For Units, Options, Fans, Wheels and Airflows, Old price
                is imported but the current Price is not: it remains empty until a later pricing update, such as Sync
                all rooftops prices. QM and Controls import their Price directly. Other tables are not changed.
            </Typography>
            <Typography variant='h6' component='h3' sx={sectionStyles}>
                File format
            </Typography>
            <Typography>
                Accepted extensions: .xlsx, .xls and .csv (lowercase file extensions). Only the first worksheet is read.
                The first row is the header and is not imported. Download current prices can help prepare your file,
                but always check the column order below, especially for QM. Keep empty cells in place; header names
                do not remap columns.
            </Typography>
            {format ? (
                <>
                    <Typography sx={sectionStyles}>{format.note}</Typography>
                    <Typography>Example values below are illustrative: replace them with your actual data.</Typography>
                    <TableContainer>
                        <Table size='small' aria-label={`${format.title} import format and examples`}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Column (in order)</TableCell>
                                    <TableCell>Rule</TableCell>
                                    <TableCell>Example</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {format.columns.map(([name, rule, example], index) => (
                                    <TableRow key={name}>
                                        <TableCell component='th' scope='row'>
                                            {index + 1}. {name}
                                        </TableCell>
                                        <TableCell>{rule}</TableCell>
                                        <TableCell>{example === '' ? '(blank)' : example}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <Alert severity='info'>Use the destination table's download template for its exact column order.</Alert>
            )}
            <Typography variant='h6' component='h3' sx={sectionStyles}>
                Example
            </Typography>
            <Typography>
                With A, B and C in the destination table, importing a valid file containing B and D: Add new rows keeps
                A, B and C and adds D. If B has unchanged SAP metadata, the file's B is ignored. For Units, Wheels,
                Fans, Airflows and Options, if B has a changed Varcond or Kmat, B is updated in place with the
                uploaded Old price and its current price is cleared until SAP sync. Replace the whole table leaves only B and D. A and C are
                removed, and B is recreated from the file rather than updated in place.
            </Typography>
            <Box sx={sectionStyles}>
                <Alert severity='info'>
                    Review the entire file before uploading. Validation is unchanged: some tables skip invalid rows,
                    while others reject them. Never rely on skipped rows to preserve existing data. A failed import
                    does not guarantee that the previous data is preserved. Keep a backup before uploading.
                </Alert>
            </Box>
        </AppDialog>
    )
}

export default ImportHelp
